import { useEffect, useState, type FormEvent } from 'react';
import {
  INTERACTION_KIND_LABEL,
  STAKEHOLDER_TAG_LABEL,
  STAKEHOLDER_TAG_ORDER,
  type InteractionKind,
  type StakeholderDetail,
  type StakeholderTag,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { stakeholderApi } from '../api/stakeholder.api';

/**
 * One external contact: their details, the notepad, and the interaction
 * timeline.
 *
 * The timeline is the point of the module. A contact record nobody logs
 * against is a stale address book; the log is what makes "when did we last
 * speak to this donor" answerable, so logging is the first thing in reach
 * rather than buried behind an edit form.
 */

/** Tag chips. Distinct hues, each always paired with its label. */
export const TAG_CHIP: Record<StakeholderTag, string> = {
  INVESTOR: 'border-emerald-500 bg-emerald-100 text-emerald-900',
  PARTY_MEMBER: 'border-violet-600 bg-violet-100 text-violet-900',
  CONSTITUENT: 'border-sky-600 bg-sky-100 text-sky-900',
  MEDIA: 'border-fuchsia-600 bg-fuchsia-100 text-fuchsia-900',
  DONOR: 'border-amber-500 bg-amber-100 text-amber-900',
};

const KINDS: InteractionKind[] = [
  'CALL', 'MEETING', 'EMAIL', 'MESSAGE', 'EVENT', 'DONATION', 'OTHER',
];

export function contactAge(days: number | null): {
  label: string; tone: string;
} {
  if (days === null) return { label: 'Never contacted', tone: 'text-red-700' };
  if (days === 0) return { label: 'Today', tone: 'text-slate-500' };
  if (days === 1) return { label: 'Yesterday', tone: 'text-slate-500' };
  if (days < 30) return { label: `${days} days ago`, tone: 'text-slate-500' };
  if (days < 90) return { label: `${days} days ago`, tone: 'text-amber-700' };
  return { label: `${days} days ago`, tone: 'text-red-700' };
}

function dayLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export function StakeholderPanel({
  contact, onChanged, onDeleted,
}: {
  contact: StakeholderDetail;
  onChanged: (updated: StakeholderDetail) => void;
  /** The row is gone - the page closes the panel and reloads the list. */
  onDeleted: (id: string) => void;
}) {
  /**
   * View and edit are the same panel, not two screens. A contact record is
   * mostly read - you open it to check a number before a call - so editing
   * is a mode you turn on, and the timeline stays visible underneath it
   * either way.
   */
  const [editing, setEditing] = useState(false);
  // ---- log form
  const [kind, setKind] = useState<InteractionKind>('CALL');
  const [summary, setSummary] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [logging, setLogging] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  // ---- edit form, seeded from the contact each time it opens
  const [form, setForm] = useState({
    displayName: contact.displayName,
    organisationName: contact.organisationName ?? '',
    jobTitle: contact.jobTitle ?? '',
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    location: contact.location ?? '',
  });
  const [tags, setTags] = useState<StakeholderTag[]>(contact.tags);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // ---- delete, two-step like every other destructive action here
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ---- notepad
  const [notes, setNotes] = useState(contact.notes ?? '');
  const [savingNotes, setSavingNotes] = useState(false);
  const notesDirty = notes !== (contact.notes ?? '');

  const age = contactAge(contact.daysSinceContact);

  /**
   * Opening a DIFFERENT contact must not carry the previous one's draft
   * across - and leaving edit mode open on the new one would be worse
   * still, since the fields would look like theirs.
   */
  useEffect(() => {
    setEditing(false);
    setConfirmDelete(false);
    setEditError(null);
    setNotes(contact.notes ?? '');
    setForm({
      displayName: contact.displayName,
      organisationName: contact.organisationName ?? '',
      jobTitle: contact.jobTitle ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      location: contact.location ?? '',
    });
    setTags(contact.tags);
  }, [contact]);

  async function saveEdit(event: FormEvent) {
    event.preventDefault();
    if (form.displayName.trim() === '' || savingEdit) return;
    setSavingEdit(true);
    setEditError(null);
    try {
      // Empty string CLEARS the field - the API stores whitespace-only as
      // null, so "no email" is one honest check everywhere downstream.
      onChanged(await stakeholderApi.update(contact.id, {
        displayName: form.displayName.trim(),
        organisationName: form.organisationName.trim() || null,
        jobTitle: form.jobTitle.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        location: form.location.trim() || null,
        tags,
      }));
      setEditing(false);
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Those changes did not save.');
    } finally {
      setSavingEdit(false);
    }
  }

  async function remove() {
    setDeleting(true);
    try {
      await stakeholderApi.remove(contact.id);
      onDeleted(contact.id);
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Could not delete this contact.');
      setDeleting(false);
    }
  }

  async function log(event: FormEvent) {
    event.preventDefault();
    if (summary.trim() === '' || logging) return;
    setLogging(true);
    setLogError(null);
    try {
      const updated = await stakeholderApi.logInteraction(contact.id, {
        kind,
        summary: summary.trim(),
        // A date-only input means "that day", which is honest about the
        // precision people actually remember.
        ...(occurredAt ? { occurredAt: new Date(occurredAt).toISOString() } : {}),
      });
      onChanged(updated);
      setSummary('');
      setOccurredAt('');
    } catch (err) {
      setLogError(err instanceof ApiError ? err.message : 'That did not save.');
    } finally {
      setLogging(false);
    }
  }

  async function saveNotes() {
    if (!notesDirty || savingNotes) return;
    setSavingNotes(true);
    try {
      onChanged(await stakeholderApi.update(contact.id, { notes }));
    } catch {
      /* the error surfaces on the next attempt; the text is still in the box */
    } finally {
      setSavingNotes(false);
    }
  }

  return (
    <div className="space-y-5 px-6 py-5">
      {/* ---- who: read by default, edit on demand ---- */}
      {editing ? (
        <form onSubmit={(e) => void saveEdit(e)} className="space-y-3 rounded-xl border border-slate-200 p-3.5">
          <EditField label="Name" required value={form.displayName}
            onChange={(v) => setForm((f) => ({ ...f, displayName: v }))} />
          <EditField label="Organisation / party / outlet" value={form.organisationName}
            onChange={(v) => setForm((f) => ({ ...f, organisationName: v }))} />
          <EditField label="Role" value={form.jobTitle}
            onChange={(v) => setForm((f) => ({ ...f, jobTitle: v }))} />
          <EditField label="Email" type="email" value={form.email}
            onChange={(v) => setForm((f) => ({ ...f, email: v }))} />
          <EditField label="Phone" value={form.phone}
            onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
          <EditField label="Location" value={form.location}
            onChange={(v) => setForm((f) => ({ ...f, location: v }))} />

          <div>
            <span className="block text-xs font-medium text-slate-600">Tags</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {STAKEHOLDER_TAG_ORDER.map((t) => (
                <button key={t} type="button"
                  aria-pressed={tags.includes(t)}
                  onClick={() => setTags((c) =>
                    c.includes(t) ? c.filter((x) => x !== t) : [...c, t])}
                  className={[
                    'rounded-full border px-2.5 py-1 text-xs font-semibold transition',
                    tags.includes(t) ? TAG_CHIP[t] : 'border-slate-300 text-slate-500 hover:bg-slate-50',
                  ].join(' ')}>
                  {STAKEHOLDER_TAG_LABEL[t]}
                </button>
              ))}
            </div>
          </div>

          {editError && <p className="text-xs text-red-600">{editError}</p>}

          <div className="flex items-center gap-2">
            <button type="submit" disabled={savingEdit || form.displayName.trim() === ''}
              className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
              {savingEdit ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" onClick={() => setEditing(false)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </form>
      ) : (
      <div>
        <div className="flex flex-wrap items-center gap-1.5">
          {contact.tags.length === 0 ? (
            <span className="text-xs text-slate-400">No tags</span>
          ) : (
            STAKEHOLDER_TAG_ORDER.filter((t) => contact.tags.includes(t)).map((t) => (
              <span key={t}
                className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TAG_CHIP[t]}`}>
                {STAKEHOLDER_TAG_LABEL[t]}
              </span>
            ))
          )}
        </div>
        {(contact.jobTitle || contact.organisationName) && (
          <p className="mt-2 text-sm text-slate-700">
            {[contact.jobTitle, contact.organisationName].filter(Boolean).join(' · ')}
          </p>
        )}
        <dl className="mt-2 space-y-1 text-sm">
          {contact.email && (
            <div className="flex gap-2">
              <dt className="w-20 shrink-0 text-slate-400">Email</dt>
              <dd className="min-w-0 break-all text-slate-700">{contact.email}</dd>
            </div>
          )}
          {contact.phone && (
            <div className="flex gap-2">
              <dt className="w-20 shrink-0 text-slate-400">Phone</dt>
              <dd className="text-slate-700">{contact.phone}</dd>
            </div>
          )}
          {contact.location && (
            <div className="flex gap-2">
              <dt className="w-20 shrink-0 text-slate-400">Location</dt>
              <dd className="text-slate-700">{contact.location}</dd>
            </div>
          )}
          <div className="flex gap-2">
            <dt className="w-20 shrink-0 text-slate-400">Last contact</dt>
            <dd className={`font-medium ${age.tone}`}>{age.label}</dd>
          </div>
        </dl>

        {/* Edit beside a two-step delete. Destructive actions never fire on
            one click here, and the confirm says what is kept: the contact
            is soft-deleted, so its history goes with it rather than being
            shredded. */}
        <div className="mt-3 flex items-center justify-between gap-2">
          <button type="button" onClick={() => setEditing(true)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
            Edit details
          </button>
          {confirmDelete ? (
            <span className="flex items-center gap-2 text-xs">
              <span className="text-slate-600">Remove this contact?</span>
              <button type="button" disabled={deleting} onClick={() => void remove()}
                className="rounded-lg bg-red-600 px-2.5 py-1.5 font-semibold text-white transition hover:bg-red-700 disabled:opacity-50">
                {deleting ? 'Removing...' : 'Remove'}
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)}
                className="rounded-lg px-2.5 py-1.5 font-medium text-slate-500 transition hover:bg-slate-100">
                Keep
              </button>
            </span>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)}
              className="text-xs font-semibold text-red-600 transition hover:text-red-700">
              Remove contact
            </button>
          )}
        </div>
        {editError && !editing && <p className="mt-1.5 text-xs text-red-600">{editError}</p>}
      </div>
      )}

      {/* ---- log an interaction: first, not last ---- */}
      <form onSubmit={(e) => void log(e)}
        className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Log an interaction
        </p>
        <div className="mt-2 flex gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as InteractionKind)}
            aria-label="Kind of interaction"
            className="shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm outline-none focus:border-brand-500"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>{INTERACTION_KIND_LABEL[k]}</option>
            ))}
          </select>
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={500}
            placeholder="What happened?"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-[11px] text-slate-500">
            When
            <input
              type="date"
              value={occurredAt}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setOccurredAt(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs outline-none focus:border-brand-500"
            />
            <span className="text-slate-400">blank = today</span>
          </label>
          <button
            type="submit"
            disabled={logging || summary.trim() === ''}
            className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-900 disabled:opacity-40"
          >
            {logging ? 'Saving...' : 'Log it'}
          </button>
        </div>
        {logError && <p className="mt-1.5 text-xs text-red-600">{logError}</p>}
      </form>

      {/* ---- the notepad ---- */}
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Notes
          </p>
          <button
            type="button"
            onClick={() => void saveNotes()}
            disabled={!notesDirty || savingNotes}
            className="text-[11px] font-semibold text-brand-600 disabled:text-slate-300"
          >
            {savingNotes ? 'Saving...' : notesDirty ? 'Save' : 'Saved'}
          </button>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Standing context - interests, history, who introduced them."
          className="mt-1.5 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
      </div>

      {/* ---- the timeline ---- */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          History ({contact.interactionCount})
        </p>
        {contact.interactions.length === 0 ? (
          <p className="mt-2 text-xs text-slate-400">
            Nothing logged yet. The first entry is what makes this contact
            searchable by when you last spoke.
          </p>
        ) : (
          <ul className="mt-2 space-y-2.5">
            {contact.interactions.map((i) => (
              <li key={i.id} className="border-l-2 border-slate-200 pl-3">
                <p className="text-[11px] text-slate-400">
                  {dayLabel(i.occurredAt)} · {INTERACTION_KIND_LABEL[i.kind]}
                  {i.loggedByName ? ` · ${i.loggedByName}` : ''}
                </p>
                <p className="text-sm text-slate-700">{i.summary}</p>
                {i.detail && (
                  <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-500">
                    {i.detail}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** One labelled input. Local to the panel - the create form has its own,
 *  and sharing one across two files would couple their layouts. */
function EditField({
  label, value, onChange, required, type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600">
        {label}{required && <span className="ml-1 text-red-500">*</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
      />
    </label>
  );
}
